import { useEffect, useMemo, useState } from "react";

import styles from "styles/pages/reports/reports.module.scss";

import * as echarts from "echarts";

import classNames from "classnames/bind";
import ComboFilters from "../../ComboFilters";
import { If } from "shared/components/control-statements";
import { Currency } from "shared/utils/Currency";
import { Hairline, Loading } from "@components";
import { useDragScroll } from "shared/hooks/useDragScroll";
import { Numbers } from "shared/utils/Numbers";
import { OlgaEnvironmentsAPI } from "providers/OlgaEnvironmentsAPI";
import { formatNumberWithOneDecimal, Trend } from "pages/Reports/Reports";
import { useDrag } from "shared/hooks/useDrag";
const css = classNames.bind(styles);

const filters = [
  { name: "Faturamento bruto", key: "total", type: "currency" },
  { name: "Descontos totais", key: "discounts", type: "currency" },
  {
    name: "Faturamento líquido",
    key: "total_with_discounts",
    type: "currency",
  },
  { name: "Qtd. usos", key: "uses", type: "int" },
  {
    name: "Taxa de conversão",
    key: "use_ratio",
    type: "percentage",
  },
  {
    name: "Taxa de expiração",
    key: "expired_ratio",
    type: "percentage",
  },
  {
    name: "Ticket médio bruto",
    key: "average_ticket",
    type: "currency",
  },
  {
    name: "Ticket médio líquido",
    key: "average_ticket_with_discounts",
    type: "currency",
  },
  {
    name: "Tempo médio para uso",
    key: "average_time",
    type: "days",
  },
  {
    name: "ROI do cupom",
    key: "roi",
    type: "roi",
  },
];

const getFilterName = (key) => {
  const filter = filters.find((f) => f.key === key);
  return filter ? filter.name : key;
};

const getFilterType = (key) => {
  const filter = filters.find((f) => f.key === key);
  return filter ? filter.type : "string";
};

const getFormatedValue = (key, value) => {
  if (getFilterType(key) === "currency") {
    return Currency.formatCurrency(value, false, 2);
  } else if (getFilterType(key) === "int") {
    return Numbers.formatInt(value);
  } else if (getFilterType(key) === "float") {
    return Numbers.formatFloat(value, 2);
  } else if (getFilterType(key) === "percentage") {
    return Numbers.percent(+value);
  } else if (getFilterType(key) === "days") {
    return `${Numbers.formatInt(+value)} dias`;
  } else if (getFilterType(key) === "roi")
    return formatNumberWithOneDecimal(value);

  return value;
};

export default function DistribuitionUses2({
  data = {},
  isBeforeSeptember,
  isBeforeFebruary,
  reportFilters,
}) {
  const [showAll, setShowAll] = useState("see_details");
  const [selectedOption, setSelectedOption] = useState({
    name: "Cupons",
    id: "coupons",
  });
  const [selectedFilter, setSelectedFilter] = useState("total");
  const [label_dict, setLabelDict] = useState(new Map());
  const [loading, setLoading] = useState(true);

  const hasReportFilters =
    reportFilters && Object.keys(reportFilters)?.length > 0;

  const fetchData = async () => {
    const response_units = await OlgaEnvironmentsAPI.getUnits();
    const response_brands = await OlgaEnvironmentsAPI.getBrands();

    const units = response_units.getData([]);
    const brands = response_brands.getData([]);

    setLabelDict(
      new Map([
        ...units.map((unit) => [`units_${unit.id}`, unit.name]),
        ...brands.map((brand) => [`brands_${brand.id}`, brand.name]),
      ])
    );

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const idFieldMap = {
    brands: "brand_id",
    units: "unit_id",
    origins: "origin",
    coupons: "coupon_code",
  };

  const memoData = useMemo(() => data, [JSON.stringify(data)]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const chartDOM = document.getElementById("distribution-uses-chart");
      if (!chartDOM) return;

      const chart = echarts.init(chartDOM, null, {
        renderer: "svg",
      });

      const getChartData = () => {
        const idField = idFieldMap[selectedOption?.id] || "coupon_code";

        const distribution = (
          data?.[selectedOption?.id || "coupons"] || []
        )?.map((item) => {
          const itemId = item[idField];

          return {
            name:
              label_dict.get(`${selectedOption?.id || "coupons"}_${itemId}`) ||
              itemId,
            value:
              item[selectedFilter]?.value || item[selectedFilter]?.roi || 0,
          };
        });

        return distribution;
      };

      const options = {
        tooltip: {
          trigger: "item",
          responsive: true,
          formatter: (param) => {
            const data = param.data;
            return [
              `<span>${data.name}</span>
							<br/>
							<span style="opacity: 50%">
								${getFormatedValue(selectedFilter, data.value)}								
							</span>
						`,
            ].join("");
          },
          backgroundColor: "#242424",
          borderWidth: "0",
          borderColor: "transparent",
          textStyle: {
            color: "#fff",
            fontSize: 10,
          },
        },
        series: [
          {
            type: "pie",
            radius: ["75%"],
            center: ["50%", "45%"],
            label: {
              show: false,
              position: "center",
            },
            data: getChartData(),
          },
        ],
        legend: {
          type: "scroll",
          orient: "horizontal",
          pageIconColor: "#6D6DF2",
          pageIconInactiveColor: "#4C4C4C",
          backgroundColor: "#1C1C1C",
          borderRadius: 10,
          padding: 20,
          pageIconSize: 10,
          pageTextStyle: {
            color: "#B3B3B3",
            fontSize: 10,
            fontWeight: 800,
          },
          pageIcons: {
            horizontal: [
              "path://M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6z",
              "path://M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z",
            ],
          },
          top: "bottom",
          icon: "circle",
          itemWidth: 8,
          itemHeight: 8,
          itemGap: 10,
          textStyle: {
            color: "#808080",
            fontSize: 12,
          },
          inactiveColor: "#555",
          selected: Object.fromEntries(
            getChartData().map((item, index) => [item.name, index < 5])
          ),
        },
      };

      options && chart.setOption(options);

      const ro = new ResizeObserver(() => {
        chart.resize();
      });
      ro.observe(chartDOM);

      return () => {
        ro.unobserve(chartDOM);
        ro.disconnect();
        chart.dispose();
      };
    }
  }, [memoData, selectedOption, selectedFilter, showAll, label_dict]);

  const { scrollRef, onMouseDown } = useDragScroll();

  const sortByUsesDesc = (arr) =>
    arr.sort((a, b) => Number(b.uses.value) - Number(a.uses.value));

  data.brands = sortByUsesDesc(data?.brands || []);
  data.units = sortByUsesDesc(data?.units || []);
  data.origins = sortByUsesDesc(data?.origins || []);
  data.coupons = sortByUsesDesc(data?.coupons || []);

  const isDiscounts = selectedFilter === "discounts";

  useEffect(() => {
    if (selectedOption?.id !== "coupons") {
      if (
        ["average_time", "roi", "use_ratio", "expired_ratio"].includes(
          selectedFilter
        )
      )
        setSelectedFilter("total");
    }
  }, [selectedOption]);

  const scrollFiltersRef = useDrag(null);

  return (
    <div className={styles.box2} style={{ minHeight: 632 }}>
      <div className="d-flex align-center justify-between px-20">
        <div className="d-flex align-center gap-5">
          <p className={styles.label}>Distribuição de usos</p>
          <span
            className={styles.info}
            title="Distribuição dos dados dos cupons por unidade, marca e origem."
          >
            <i className="fal fa-info-circle" />
          </span>
        </div>
        {!loading && (
          <div className="d-flex align-center">
            <div className="d-flex align-center gap-10">
              <div
                className={css({
                  selectMini: true,
                  selected: showAll === "see_details",
                })}
                onClick={() => setShowAll("see_details")}
              >
                Ver detalhes
              </div>
              <div
                className={css({
                  selectMini: true,
                  selected: showAll === "see_all",
                })}
                onClick={() => setShowAll("see_all")}
              >
                Ver tudo
              </div>
            </div>
          </div>
        )}
      </div>

      <If cond={loading}>
        <Loading fullContent />
      </If>

      <If cond={showAll === "see_details"}>
        <div className="mb-15 mt-15">
          <div className={styles.boxFilters} ref={scrollFiltersRef}>
            {filters.map((filter) => {
              if (selectedOption?.id !== "coupons") {
                if (
                  [
                    "average_time",
                    "roi",
                    "use_ratio",
                    "expired_ratio",
                  ].includes(filter.key)
                )
                  return null;
              }

              return (
                <div
                  key={filter.key}
                  className={css({
                    filter: true,
                    selected: selectedFilter === filter.key,
                  })}
                  onClick={() => setSelectedFilter(filter.key)}
                >
                  {filter.name}
                </div>
              );
            })}
          </div>
        </div>
      </If>

      <If cond={showAll === "see_all"}>
        <Hairline className="mt-15" />
      </If>

      <div className={styles.container2}>
        <If cond={showAll === "see_details"}>
          <div className="px-20 d-flex w-100">
            <div
              className={styles.tableOrdersDistribution}
              onMouseDown={onMouseDown}
              ref={scrollRef}
            >
              <table>
                <thead>
                  <tr>
                    <th>
                      <div className="d-flex">
                        <ComboFilters
                          options={[
                            { name: "Cupons", id: "coupons" },
                            { name: "Unidades", id: "units" },
                            { name: "Marcas", id: "brands" },
                            { name: "Origens", id: "origins" },
                          ]}
                          design="two"
                          onSelect={(option) => setSelectedOption(option)}
                          selectedOption={
                            selectedOption || { name: "Cupons", id: "coupons" }
                          }
                        />
                      </div>
                    </th>
                    <th style={{ width: 200 }}>
                      {getFilterName(selectedFilter || "uses")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data?.[selectedOption?.id || "coupons"]?.map(
                    (item, index) => {
                      return (
                        <tr key={index}>
                          <td>
                            {(() => {
                              const idField =
                                idFieldMap[selectedOption?.id] || "coupon_code";
                              const itemId = item[idField];

                              return (
                                label_dict.get(
                                  `${selectedOption?.id || "coupons"}_${itemId}`
                                ) ||
                                itemId ||
                                "Origem desconhecida"
                              );
                            })()}
                          </td>
                          <td>
                            <div className="d-flex align-center">
                              <p className={styles.value}>
                                {(isDiscounts && isBeforeSeptember) ||
                                (isBeforeFebruary &&
                                  [
                                    "discounts",
                                    "total_with_discounts",
                                    "average_ticket_with_discounts",
                                    "roi",
                                  ].includes(selectedFilter))
                                  ? "---"
                                  : getFormatedValue(
                                      selectedFilter,
                                      item[selectedFilter]?.value ||
                                        item[selectedFilter]?.roi ||
                                        0
                                    )}
                              </p>
                            </div>
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>

            <div className={styles.chartContainer}>
              <p className={styles.title}>
                Proporção de {getFilterName(selectedFilter || "order_quantity")}
              </p>
              <If cond={isBeforeSeptember && isDiscounts}>
                <div className={styles.emptyChart} style={{ height: 420 }}>
                  <span className="text-grey">
                    Não há dados para serem exibidos
                  </span>
                </div>
              </If>
              <div
                id="distribution-uses-chart"
                style={{
                  width: "100%",
                  height: "100%",
                  minHeight: 350,
                  margin: "auto",
                }}
              />
            </div>
          </div>
        </If>

        <If cond={showAll === "see_all"}>
          <div
            className={styles.tableOrdersDistributionFull}
            onMouseDown={onMouseDown}
            ref={scrollRef}
            style={{ paddingRight: 0 }}
          >
            <table>
              <thead>
                <tr>
                  <th
                    style={{
                      position: "sticky",
                      left: 0,
                      zIndex: 200,
                      backgroundColor: "#1f1f1f",
                    }}
                  >
                    <div className="d-flex">
                      <ComboFilters
                        options={[
                          { name: "Cupons", id: "coupons" },
                          { name: "Unidades", id: "units" },
                          { name: "Marcas", id: "brands" },
                          { name: "Origens", id: "origins" },
                        ]}
                        design="two"
                        onSelect={(option) => setSelectedOption(option)}
                        selectedOption={
                          selectedOption || { name: "Cupons", id: "coupons" }
                        }
                      />
                    </div>
                  </th>
                  <th style={{ paddingLeft: 35 }}>FATURAMENTO BRUTO</th>
                  <th style={{ paddingLeft: 35 }}>DESCONTOS TOTAIS</th>
                  <th style={{ paddingLeft: 35 }}>FATURAMENTO LÍQUIDO</th>
                  <th style={{ paddingLeft: 35 }}>QTD. USOS</th>
                  <If cond={selectedOption?.id === "coupons"}>
                    <th style={{ paddingLeft: 35 }}>TAXA DE CONVERSÃO</th>
                    <th style={{ paddingLeft: 35 }}>TAXA DE EXPIRAÇÃO</th>
                  </If>
                  <th style={{ paddingLeft: 35 }}>TICKET MÉDIO BRUTO</th>
                  <th style={{ paddingLeft: 35 }}>TICKET MÉDIO LÍQUIDO</th>
                  <If cond={selectedOption?.id === "coupons"}>
                    <th style={{ paddingLeft: 35 }}>Tempo médio para uso</th>
                    <th style={{ paddingLeft: 35 }}>ROI DO CUPOM</th>
                  </If>
                </tr>
              </thead>
              <tbody>
                {data?.[selectedOption?.id || "coupons"]?.map((item, index) => {
                  return (
                    <tr key={index}>
                      <td
                        style={{
                          position: "sticky",
                          left: 0,
                          zIndex: 3,
                          backgroundColor: "#1c1c1c",
                        }}
                      >
                        {(() => {
                          const idField =
                            idFieldMap[selectedOption?.id] || "coupon_code";
                          const itemId = item[idField];

                          return (
                            label_dict.get(
                              `${selectedOption?.id || "coupons"}_${itemId}`
                            ) || itemId
                          );
                        })()}
                      </td>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {Currency.formatCurrency(
                              item?.total?.value,
                              false,
                              2
                            )}
                          </p>
                        </div>
                      </td>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {isBeforeSeptember || isBeforeFebruary
                              ? "---"
                              : Currency.formatCurrency(item?.discounts?.value)}
                          </p>
                        </div>
                      </td>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {isBeforeSeptember || isBeforeFebruary
                              ? "---"
                              : Currency.formatCurrency(
                                  item?.total_with_discounts?.value
                                )}
                          </p>
                        </div>
                      </td>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {Numbers.formatInt(item?.uses?.value)}
                          </p>
                        </div>
                      </td>

                      <If cond={selectedOption?.id === "coupons"}>
                        <td>
                          <div
                            className="d-flex align-center"
                            style={{ paddingLeft: 20 }}
                          >
                            <p className={styles.value}>
                              {Numbers.percent(item?.use_ratio?.value)}
                            </p>
                          </div>
                        </td>

                        <td>
                          <div
                            className="d-flex align-center"
                            style={{ paddingLeft: 20 }}
                          >
                            <p className={styles.value}>
                              {Numbers.percent(item?.expired_ratio?.value)}
                            </p>
                          </div>
                        </td>
                      </If>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {Currency.formatCurrency(
                              item?.average_ticket?.value
                            )}
                          </p>
                        </div>
                      </td>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {isBeforeFebruary
                              ? "---"
                              : Currency.formatCurrency(
                                  item?.average_ticket_with_discounts?.value
                                )}
                          </p>
                        </div>
                      </td>
                      <If cond={selectedOption?.id === "coupons"}>
                        <td>
                          <div
                            className="d-flex align-center"
                            style={{ paddingLeft: 20 }}
                          >
                            <p className={styles.value}>
                              {Numbers.formatInt(item?.average_time?.value)}{" "}
                              dias
                            </p>
                          </div>
                        </td>
                        <td>
                          <div
                            className="d-flex align-center"
                            style={{ paddingLeft: 20 }}
                          >
                            <p className={styles.value}>
                              {isBeforeSeptember || isBeforeFebruary
                                ? "---"
                                : Numbers.formatFloat(item?.roi?.roi)}
                            </p>
                          </div>
                        </td>
                      </If>
                    </tr>
                  );
                })}
                {Array.from({
                  length: Math.max(
                    0,
                    12 - data?.[selectedOption?.id || "coupons"]?.length
                  ),
                }).map((_, i) => (
                  <tr key={`empty-${i}`}>
                    <td
                      style={{
                        position: "sticky",
                        left: 0,
                        zIndex: 3,
                        backgroundColor: "#1c1c1c",
                      }}
                    >
                      &nbsp;
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </If>
      </div>
    </div>
  );
}
